import bankJson from '../data/earTrainingMelodies.json';
import type {
  EarTrainingClip,
  EarTrainingMelody,
  EarTrainingMelodyBank,
  EarTrainingMode,
} from '../types/earTraining';
import {
  pickRandomEarTrainingKey,
  semitonesBetweenTonics,
} from '../utils/earTrainingKeys';
import { melodyUsesOnlyDiatonicScaleDegrees } from '../utils/earTrainingDifficulty';
import { firstNoteSyllable } from '../utils/earTrainingSolfegeAnswer';
import { tonicToPitchClass } from '../utils/pitchLabel';
import { semitoneStepForSyllable } from '../utils/solfege';

const bank = bankJson as EarTrainingMelodyBank;

export function getEarTrainingBank(): ReadonlyArray<EarTrainingMelody> {
  return bank.melodies;
}

export function clampDifficulty(level: number): number {
  return Math.max(1, Math.min(10, Math.round(level)));
}

export function melodiesAtDifficulty(level: number): EarTrainingMelody[] {
  const n = clampDifficulty(level);
  return bank.melodies.filter(m => m.difficulty === n);
}

/** Melodies at or below the chosen difficulty (for counts / fallback). */
export function melodiesUpToDifficulty(maxDifficulty: number): EarTrainingMelody[] {
  const max = clampDifficulty(maxDifficulty);
  return bank.melodies.filter(m => m.difficulty <= max);
}

function filterMelodiesForLevel(
  melodies: EarTrainingMelody[],
  maxDifficulty: number
): EarTrainingMelody[] {
  if (maxDifficulty > 5) return melodies;
  return melodies.filter(melodyUsesOnlyDiatonicScaleDegrees);
}

/** Prefer exact level; fall back to easier if none at that rank. Levels 1–5: diatonic major/minor only. */
export function pickRandomMelody(maxDifficulty = 10): EarTrainingMelody {
  const max = clampDifficulty(maxDifficulty);
  let list = filterMelodiesForLevel(melodiesAtDifficulty(max), max);
  if (list.length === 0) {
    list = filterMelodiesForLevel(melodiesUpToDifficulty(max), max);
  }
  if (list.length === 0) {
    throw new Error(`No ear training melodies at difficulty ≤ ${max}.`);
  }
  return list[Math.floor(Math.random() * list.length)]!;
}

/** Transpose playback; tonic and key signature stay as stored on the melody. */
export function clipWithTranspose(
  melody: EarTrainingMelody,
  semitones: number
): EarTrainingClip {
  return {
    ...melody,
    transposeSemitones: semitones,
  };
}

/** Transpose so the first sounding note matches the requested syllable in the written key. */
export function clipWithStartingSyllable(
  melody: EarTrainingMelody,
  semitones: number,
  startingSyllable: string
): EarTrainingClip {
  const step = semitoneStepForSyllable(startingSyllable, melody.mode);
  if (step === null) {
    return clipWithTranspose(melody, semitones);
  }
  const firstMidi = melody.notes[0]!.midi + semitones;
  const firstPc = ((firstMidi % 12) + 12) % 12;
  const keyPc = tonicToPitchClass(melody.tonic);
  const desiredPc = (keyPc + step + 120) % 12;
  let extra = desiredPc - firstPc;
  if (extra > 6) extra -= 12;
  if (extra < -6) extra += 12;
  return clipWithTranspose(melody, semitones + extra);
}

const NON_DO_START_SYLLABLES_MAJOR = ['re', 'mi', 'fa', 'sol', 'la', 'ti'] as const;
const NON_DO_START_SYLLABLES_MINOR = ['ti', 're', 'mi', 'fa', 'sol', 'la'] as const;

/** Level 1 always starts on do; level 2+ uses do 20% and another syllable otherwise. */
export function pickTargetStartingSyllable(
  difficulty: number,
  mode: EarTrainingMode,
  random = Math.random
): string {
  if (difficulty <= 1) return 'do';
  if (random() < 0.2) return 'do';
  const pool =
    mode === 'major' ? NON_DO_START_SYLLABLES_MAJOR : NON_DO_START_SYLLABLES_MINOR;
  return pool[Math.floor(random() * pool.length)]!;
}

export function pickRandomClip(maxDifficulty = 10): EarTrainingClip {
  const m = pickRandomMelody(maxDifficulty);
  const key = pickRandomEarTrainingKey(m.mode);
  const semitones = semitonesBetweenTonics(m.tonic, key.tonic);
  const keyed: EarTrainingMelody = {
    ...m,
    tonic: key.tonic,
    keySignatureFifths: key.keySignatureFifths,
  };
  const target = pickTargetStartingSyllable(m.difficulty, m.mode);
  return clipWithStartingSyllable(keyed, semitones, target);
}

/** How many bank tunes exist at each difficulty rank. */
export function difficultyCounts(): Record<number, number> {
  const counts: Record<number, number> = {};
  for (let d = 1; d <= 10; d++) counts[d] = 0;
  for (const m of bank.melodies) {
    counts[m.difficulty] = (counts[m.difficulty] ?? 0) + 1;
  }
  return counts;
}
